import {
  MAX_TOOL_CHAIN_DEPTH,
  inspectToolChainDepth,
  toolChainSchema,
  type ToolName,
  type ToolParameters,
  type ParsedToolChain,
} from '@galapagos/shared/src/flow/tools';

/**
 * A host-owned function bound to one tool name. Receives the validated,
 * catalog-typed parameters for that tool (the `z.output` of its catalog
 * `parametersSchema`) — the dispatcher has already parsed and defaulted them.
 */
export type ToolHandler<K extends ToolName = ToolName> = (
  parameters: ToolParameters<K>,
) => unknown | Promise<unknown>;

/** DI map of host handlers keyed by tool name. Hosts bind only what they expose. */
export type ToolHandlers = { [K in ToolName]?: ToolHandler<K> };

export type ToolPreflightResult =
  | { ok: true; tools: ParsedToolChain }
  | { ok: false; error: ToolValidationError };

/** Raised when a tool call fails schema validation or has no bound handler. */
export class ToolValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ToolValidationError';
  }
}

/**
 * Validate a complete tool tree and every handler binding without executing it.
 * Accepts `unknown[]` so untrusted trees (e.g. raw provider input) reach the
 * same schema boundary as TS-authored `ToolCall[]` chains without a cast.
 */
export function preflightToolChain(tools: unknown[], handlers: ToolHandlers): ToolPreflightResult {
  const inspection = inspectToolChainDepth(tools);
  if (!inspection.ok) {
    return {
      ok: false,
      error: new ToolValidationError(toolTreeInspectionMessage(inspection)),
    };
  }

  const parsed = toolChainSchema.safeParse(tools);
  if (!parsed.success) {
    return {
      ok: false,
      error: new ToolValidationError(`Invalid tool call: ${parsed.error.message}`),
    };
  }

  const unboundTool = findUnboundTool(parsed.data, handlers);
  if (unboundTool) {
    return {
      ok: false,
      error: new ToolValidationError(`No handler bound for tool "${unboundTool}"`),
    };
  }

  return { ok: true, tools: parsed.data };
}

function toolTreeInspectionMessage(
  inspection: Exclude<ReturnType<typeof inspectToolChainDepth>, { ok: true }>,
): string {
  if (inspection.reason === 'cycle') return 'Tool chains cannot contain circular branches';
  return `Tool chains cannot exceed ${MAX_TOOL_CHAIN_DEPTH} nested branch levels`;
}

function findUnboundTool(chain: ParsedToolChain, handlers: ToolHandlers): ToolName | undefined {
  for (const call of chain) {
    if (typeof handlers[call.tool_name] !== 'function') return call.tool_name;

    const successUnbound = call.onSuccess ? findUnboundTool(call.onSuccess, handlers) : undefined;
    if (successUnbound) return successUnbound;

    const errorUnbound = call.onError ? findUnboundTool(call.onError, handlers) : undefined;
    if (errorUnbound) return errorUnbound;
  }

  return undefined;
}
